/**
 * Session Store
 *
 * Manages quiz session state including session ID, stats, and connection status.
 * Sessions are persisted in Redis via the backend.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface SessionStats {
  totalQuestions: number;
  correctAnswers: number;
  byType?: Record<string, { total: number; correct: number }>;
}

interface SessionState {
  // State
  sessionId: string | null;
  stats: SessionStats;
  isInitialized: boolean;
  isCreating: boolean;
  error: string | null;

  // Actions
  createSession: () => Promise<void>;
  restoreSession: (sessionId: string) => Promise<void>;
  updateStats: (stats: SessionStats) => void;
  clearSession: () => void;
  setError: (error: string | null) => void;
}

const STORAGE_KEY = 'quizzler_session_id';

// Get stored session ID from localStorage
const getStoredSessionId = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

// Store session ID to localStorage
const storeSessionId = (sessionId: string | null) => {
  try {
    if (sessionId) {
      localStorage.setItem(STORAGE_KEY, sessionId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
};

export const useSessionStore = create<SessionState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        sessionId: getStoredSessionId(),
        stats: {
          totalQuestions: 0,
          correctAnswers: 0,
        },
        isInitialized: false,
        isCreating: false,
        error: null,

        // Create a new session via REST API
        createSession: async () => {
          const { isCreating, sessionId } = get();

          // If already creating or have a valid session, skip
          if (isCreating) return;

          // Try to restore existing session first
          if (sessionId) {
            try {
              await get().restoreSession(sessionId);
              return;
            } catch {
              // Session invalid, create new one
            }
          }

          set({ isCreating: true, error: null });

          try {
            const response = await fetch('/api/sessions', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({}),
            });

            if (!response.ok) {
              throw new Error('Failed to create session');
            }

            const data = await response.json();
            const newSessionId = data.data.id;

            storeSessionId(newSessionId);
            set({
              sessionId: newSessionId,
              stats: data.data.stats || { totalQuestions: 0, correctAnswers: 0 },
              isInitialized: true,
              isCreating: false,
            });
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create session';
            set({ error: message, isCreating: false });
            // Continue without session - graceful degradation
            set({ isInitialized: true });
          }
        },

        // Restore an existing session
        restoreSession: async (sessionId) => {
          set({ isCreating: true, error: null });

          try {
            const response = await fetch(`/api/sessions/${sessionId}`, {
              headers: {
                'X-Session-ID': sessionId,
              },
            });

            if (!response.ok) {
              throw new Error('Session not found');
            }

            const data = await response.json();

            set({
              sessionId,
              stats: data.data.stats || { totalQuestions: 0, correctAnswers: 0 },
              isInitialized: true,
              isCreating: false,
            });
          } catch (error) {
            // Session not found, clear stored ID and create new
            storeSessionId(null);
            set({ sessionId: null, isCreating: false });
            throw error;
          }
        },

        // Update stats (called after answer submission)
        updateStats: (stats) => {
          set({ stats });
        },

        // Clear session (e.g., on logout or reset)
        clearSession: () => {
          storeSessionId(null);
          set({
            sessionId: null,
            stats: { totalQuestions: 0, correctAnswers: 0 },
            isInitialized: false,
            error: null,
          });
        },

        // Set error state
        setError: (error) => {
          set({ error });
        },
      }),
      {
        name: 'session-store',
        partialize: (state) => ({ sessionId: state.sessionId }),
      }
    ),
    { name: 'session-store' }
  )
);

// Selectors
export const selectSessionId = (state: SessionState) => state.sessionId;
export const selectStats = (state: SessionState) => state.stats;
export const selectIsInitialized = (state: SessionState) => state.isInitialized;
export const selectAccuracy = (state: SessionState) => {
  const { totalQuestions, correctAnswers } = state.stats;
  if (totalQuestions === 0) return 0;
  return Math.round((correctAnswers / totalQuestions) * 100);
};
